/**
 * Animated accordions using native <details> with height transitions on open and close.
 */

const initializedAccordions = new WeakSet();

function getTransitionMs(content) {
  const duration = getComputedStyle(content).transitionDuration;
  const first = duration.split(',')[0].trim();
  const value = parseFloat(first);

  if (Number.isNaN(value)) {
    return 300;
  }

  return first.endsWith('ms') ? value : value * 1000;
}

function afterHeightTransition(content, callback) {
  const ms = getTransitionMs(content);
  const fallback = window.setTimeout(callback, ms + 50);

  const onEnd = (event) => {
    if (event.propertyName !== 'height') {
      return;
    }

    window.clearTimeout(fallback);
    content.removeEventListener('transitionend', onEnd);
    callback();
  };

  content.addEventListener('transitionend', onEnd);
}

function syncInitialState(details, content) {
  if (details.open) {
    content.style.height = 'auto';
    content.classList.remove('overflow-hidden');
  } else {
    content.style.height = '0';
    content.classList.add('overflow-hidden');
  }
}

function expand(details, content, onComplete) {
  details.open = true;

  content.classList.add('overflow-hidden');
  content.style.height = '0';
  void content.offsetHeight;

  const fullHeight = content.scrollHeight;

  requestAnimationFrame(() => {
    content.style.height = `${fullHeight}px`;
  });

  afterHeightTransition(content, () => {
    content.style.height = 'auto';
    content.classList.remove('overflow-hidden');
    onComplete();
  });
}

function collapse(details, content, onComplete) {
  const currentHeight = content.scrollHeight;
  content.classList.add('overflow-hidden');
  content.style.height = `${currentHeight}px`;
  void content.offsetHeight;

  requestAnimationFrame(() => {
    content.style.height = '0';
  });

  afterHeightTransition(content, () => {
    details.open = false;
    content.style.height = '0';
    onComplete();
  });
}

function initAccordion(details) {
  if (initializedAccordions.has(details)) {
    return;
  }

  const summary = details.querySelector('summary');
  const content = details.querySelector('.accordion__content');

  if (!summary || !content) {
    return;
  }

  initializedAccordions.add(details);
  syncInitialState(details, content);

  summary.addEventListener('click', (event) => {
    event.preventDefault();

    if (details.dataset.accordionAnimating === 'true') {
      return;
    }

    const isOpen = details.open;
    details.dataset.accordionAnimating = 'true';

    const finish = () => {
      details.dataset.accordionAnimating = 'false';
    };

    if (isOpen) {
      collapse(details, content, finish);
    } else {
      expand(details, content, finish);
    }
  });
}

export function initAccordions(root = document) {
  root.querySelectorAll('details[data-accordion]').forEach(initAccordion);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAccordions());
} else {
  initAccordions();
}

document.addEventListener('shopify:section:load', (event) => {
  initAccordions(event.target);
});
